import { drag } from 'd3-drag';
import {
  type GeoPermissibleObjects,
  geoGraticule,
  geoOrthographic,
  geoPath,
} from 'd3-geo';
import { type BaseType, type Selection, select } from 'd3-selection';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';

import worldTopology from '../data/world-110m.json';

/** Latitudinal tilt (degrees) — negative phi reveals the north pole. */
export const NORTH_POLE_TILT = -30;

const DRAG_SENSITIVITY = 0.35;

export const GLOBE_COLORS = {
  navy: '#000066',
  lightNavy: '#dfdfec',
  graticule: 'rgba(0, 0, 102, 0.1)',
} as const;

export const worldLand = feature(
  worldTopology as unknown as Topology,
  (worldTopology as unknown as Topology).objects.countries,
);

export type GlobeProjection = ReturnType<typeof geoOrthographic>;
export type GlobeSvg = Selection<SVGSVGElement, unknown, BaseType, unknown>;
export type GlobeGroup = Selection<SVGGElement, unknown, BaseType, unknown>;

export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

export const createGlobeProjection = (): GlobeProjection =>
  geoOrthographic().clipAngle(90);

export const setAxisRotation = (
  projection: GlobeProjection,
  lng: number,
): void => {
  projection.rotate([-lng, NORTH_POLE_TILT, 0]);
};

export const fitGlobeProjection = (
  projection: GlobeProjection,
  width: number,
  height: number,
): void => {
  const size = Math.min(width, height);
  projection.scale(size / 2.08).translate([width / 2, height / 2]);
};

export const renderOrthographicGlobe = (
  globeGroup: GlobeGroup,
  projection: GlobeProjection,
  width: number,
  height: number,
): void => {
  const path = geoPath(projection);

  globeGroup.selectAll('*').remove();

  globeGroup
    .append('path')
    .datum(geoGraticule()())
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', GLOBE_COLORS.graticule)
    .attr('stroke-width', 0.6);

  globeGroup
    .append('path')
    .datum(worldLand as GeoPermissibleObjects)
    .attr('d', path)
    .attr('fill', GLOBE_COLORS.lightNavy);

  globeGroup
    .append('circle')
    .attr('cx', width / 2)
    .attr('cy', height / 2)
    .attr('r', projection.scale())
    .attr('fill', 'none')
    .attr('stroke', GLOBE_COLORS.graticule)
    .attr('stroke-width', 1);
};

export const enableAxisDrag = (
  svg: GlobeSvg,
  projection: GlobeProjection,
  onRender: () => void,
): void => {
  const dragBehavior = drag<SVGSVGElement, unknown>()
    .on('start', (event) => {
      select(event.sourceEvent.target as Element).style('cursor', 'grabbing');
    })
    .on('drag', (event) => {
      const rotate = projection.rotate();
      projection.rotate([
        rotate[0] + event.dx * DRAG_SENSITIVITY,
        NORTH_POLE_TILT,
        rotate[2],
      ]);
      onRender();
    })
    .on('end', () => {
      svg.style('cursor', 'grab');
    });

  svg.style('cursor', 'grab').call(dragBehavior);
};

export const disableAxisDrag = (svg: GlobeSvg): void => {
  svg.on('.drag', null).style('cursor', null);
};
